import React, { useState, useEffect } from 'react';
import { Settings, Wrench, Clock, Mail, Twitter, Facebook, Linkedin, AlertCircle } from 'lucide-react';

const MaintenanceMode = () => {
  const [countdown, setCountdown] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  // Estimated completion time (adjust as needed)
  const estimatedCompletion = new Date();
  estimatedCompletion.setHours(estimatedCompletion.getHours() + 2); // 2 hours from now

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = estimatedCompletion.getTime() - now;

      if (distance > 0) {
        setCountdown({
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
      padding: '20px',
      position: 'relative',
      overflow: 'hidden',
    },
    grid: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      backgroundImage: `
        linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
      `,
      backgroundSize: '50px 50px',
      opacity: 0.3,
    },
    contentWrapper: {
      backgroundColor: '#ffffff',
      borderRadius: '24px',
      padding: '60px 40px',
      maxWidth: '700px',
      width: '100%',
      textAlign: 'center',
      position: 'relative',
      zIndex: 1,
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
    },
    iconWrapper: {
      marginBottom: '32px',
      position: 'relative',
      display: 'inline-block',
    },
    iconCircle: {
      width: '120px',
      height: '120px',
      borderRadius: '50%',
      backgroundColor: '#f59e0b',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto',
      position: 'relative',
      animation: 'rotate 3s linear infinite',
    },
    innerCircle: {
      width: '100px',
      height: '100px',
      borderRadius: '50%',
      backgroundColor: '#ffffff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    badge: {
      position: 'absolute',
      top: '-10px',
      right: '-10px',
      backgroundColor: '#ef4444',
      color: 'white',
      padding: '8px 16px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 'bold',
      textTransform: 'uppercase',
      boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
    },
    title: {
      fontSize: '36px',
      fontWeight: 'bold',
      color: '#1e293b',
      marginBottom: '16px',
    },
    subtitle: {
      fontSize: '20px',
      color: '#f59e0b',
      marginBottom: '24px',
      fontWeight: '600',
    },
    description: {
      fontSize: '16px',
      color: '#64748b',
      marginBottom: '40px',
      lineHeight: '1.6',
      maxWidth: '500px',
      margin: '0 auto 40px',
    },
    countdownContainer: {
      display: 'flex',
      justifyContent: 'center',
      gap: '16px',
      marginBottom: '40px',
    },
    countdownBox: {
      backgroundColor: '#f8fafc',
      padding: '20px',
      borderRadius: '12px',
      minWidth: '100px',
      border: '2px solid #e2e8f0',
    },
    countdownNumber: {
      fontSize: '32px',
      fontWeight: 'bold',
      color: '#f59e0b',
      display: 'block',
      marginBottom: '8px',
    },
    countdownLabel: {
      fontSize: '14px',
      color: '#64748b',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    infoBox: {
      backgroundColor: '#fef3c7',
      border: '1px solid #fbbf24',
      padding: '20px',
      borderRadius: '12px',
      marginBottom: '32px',
    },
    infoTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#92400e',
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    },
    infoText: {
      fontSize: '14px',
      color: '#78350f',
      lineHeight: '1.6',
    },
    updatesList: {
      textAlign: 'left',
      backgroundColor: '#f8fafc',
      padding: '20px',
      borderRadius: '12px',
      marginBottom: '32px',
    },
    updatesTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1e293b',
      marginBottom: '16px',
    },
    updateItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      marginBottom: '12px',
      fontSize: '14px',
      color: '#64748b',
    },
    updateIcon: {
      marginTop: '2px',
      flexShrink: 0,
    },
    contactBox: {
      marginBottom: '24px',
    },
    contactTitle: {
      fontSize: '14px',
      color: '#64748b',
      marginBottom: '12px',
    },
    contactEmail: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      color: '#3b82f6',
      textDecoration: 'none',
      fontSize: '16px',
      fontWeight: '500',
      padding: '12px 24px',
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
      transition: 'all 0.2s',
    },
    socialLinks: {
      display: 'flex',
      justifyContent: 'center',
      gap: '16px',
    },
    socialIcon: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      backgroundColor: '#f8fafc',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s',
      border: '1px solid #e2e8f0',
    },
    footer: {
      marginTop: '32px',
      paddingTop: '24px',
      borderTop: '1px solid #e2e8f0',
      fontSize: '12px',
      color: '#94a3b8',
    },
  };

  // Add CSS animations
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.textContent = `
      @keyframes rotate {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }

      @media (max-width: 640px) {
        .countdown-container { flex-wrap: wrap !important; }
        .countdown-box { min-width: 80px !important; }
      }
    `;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);

  return (
    <div style={styles.container}>
      <div style={styles.grid} />

      <div style={styles.contentWrapper}>
        <div style={styles.iconWrapper}>
          <div style={styles.iconCircle}>
            <div style={styles.innerCircle}>
              <Settings size={48} color="#f59e0b" />
            </div>
          </div>
          <div style={styles.badge}>LIVE</div>
        </div>

        <h1 style={styles.title}>Under Maintenance</h1>
        <h2 style={styles.subtitle}>We'll Be Back Soon!</h2>
        <p style={styles.description}>
          Our system is currently undergoing scheduled maintenance to improve your experience. 
          We apologize for any inconvenience and appreciate your patience.
        </p>

        <div className="countdown-container" style={styles.countdownContainer}>
          <div className="countdown-box" style={styles.countdownBox}>
            <span style={styles.countdownNumber}>{String(countdown.hours).padStart(2, '0')}</span>
            <span style={styles.countdownLabel}>Hours</span>
          </div>
          <div className="countdown-box" style={styles.countdownBox}>
            <span style={styles.countdownNumber}>{String(countdown.minutes).padStart(2, '0')}</span>
            <span style={styles.countdownLabel}>Minutes</span>
          </div>
          <div className="countdown-box" style={styles.countdownBox}>
            <span style={styles.countdownNumber}>{String(countdown.seconds).padStart(2, '0')}</span>
            <span style={styles.countdownLabel}>Seconds</span>
          </div>
        </div>

        <div style={styles.infoBox}>
          <div style={styles.infoTitle}>
            <AlertCircle size={20} />
            What's Happening?
          </div>
          <p style={styles.infoText}>
            We're performing essential system upgrades, security patches, and performance improvements 
            to ensure you have the best possible experience.
          </p>
        </div>

        <div style={styles.updatesList}>
          <div style={styles.updatesTitle}>
            <Wrench size={18} style={{ display: 'inline', marginRight: '8px' }} />
            Maintenance Activities:
          </div>
          <div style={styles.updateItem}>
            <Clock size={16} color="#f59e0b" style={styles.updateIcon} />
            <span>Database optimization and indexing</span>
          </div>
          <div style={styles.updateItem}>
            <Clock size={16} color="#f59e0b" style={styles.updateIcon} />
            <span>Security updates and patches</span>
          </div>
          <div style={styles.updateItem}>
            <Clock size={16} color="#f59e0b" style={styles.updateIcon} />
            <span>Performance enhancements</span>
          </div>
          <div style={styles.updateItem}>
            <Clock size={16} color="#f59e0b" style={styles.updateIcon} />
            <span>New features deployment</span>
          </div>
        </div>

        <div style={styles.contactBox}>
          <div style={styles.contactTitle}>Need urgent assistance?</div>
          <a
            href="mailto:support@tracklio.com"
            style={styles.contactEmail}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#3b82f6';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f8fafc';
              e.currentTarget.style.color = '#3b82f6';
            }}
          >
            <Mail size={20} />
            support@tracklio.com
          </a>
        </div>

        <div style={styles.socialLinks}>
          <div
            style={styles.socialIcon}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
          >
            <Twitter size={20} color="#64748b" />
          </div>
          <div
            style={styles.socialIcon}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1877f2'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
          >
            <Facebook size={20} color="#64748b" />
          </div>
          <div
            style={styles.socialIcon}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0a66c2'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
          >
            <Linkedin size={20} color="#64748b" />
          </div>
        </div>

        <div style={styles.footer}>
          © 2025 TRACKLIO. All rights reserved. | Estimated completion: {estimatedCompletion.toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
};

export default MaintenanceMode;